import { useState, useCallback } from 'react';
import {
  Type,
  Heading1,
  Image as ImageIcon,
  RectangleHorizontal,
  Minus,
  MoveVertical,
  Code2,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  Palette,
  GripVertical,
  Settings2,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';

// ── Types ──

type FontFamily =
  | 'MODERN_SANS'
  | 'BOOK_SANS'
  | 'ORGANIC_SANS'
  | 'GEOMETRIC_SANS'
  | 'HEAVY_SANS'
  | 'ROUNDED_SANS'
  | 'MODERN_SERIF'
  | 'BOOK_SERIF'
  | 'MONOSPACE';

type TextAlign = 'left' | 'center' | 'right';

interface Padding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// Block data shapes matching @usewaypoint/email-builder schemas
interface EmailLayoutData {
  backdropColor?: string | null;
  canvasColor?: string | null;
  textColor?: string | null;
  borderColor?: string | null;
  borderRadius?: number | null;
  fontFamily?: FontFamily | null;
  childrenIds?: string[] | null;
}

interface TextBlockData {
  style?: {
    color?: string | null;
    backgroundColor?: string | null;
    fontSize?: number | null;
    fontFamily?: FontFamily | null;
    fontWeight?: 'bold' | 'normal' | null;
    textAlign?: TextAlign | null;
    padding?: Padding | null;
  } | null;
  props?: {
    text?: string | null;
    markdown?: boolean | null;
  } | null;
}

interface HeadingBlockData {
  style?: {
    color?: string | null;
    backgroundColor?: string | null;
    fontFamily?: FontFamily | null;
    fontWeight?: 'bold' | 'normal' | null;
    textAlign?: TextAlign | null;
    padding?: Padding | null;
  } | null;
  props?: {
    text?: string | null;
    level?: 'h1' | 'h2' | 'h3' | null;
  } | null;
}

interface ImageBlockData {
  style?: {
    padding?: Padding | null;
    backgroundColor?: string | null;
    textAlign?: TextAlign | null;
  } | null;
  props?: {
    url?: string | null;
    alt?: string | null;
    width?: number | null;
    height?: number | null;
    linkHref?: string | null;
    contentAlignment?: 'top' | 'middle' | 'bottom' | null;
  } | null;
}

interface ButtonBlockData {
  style?: {
    backgroundColor?: string | null;
    fontSize?: number | null;
    fontFamily?: FontFamily | null;
    fontWeight?: 'bold' | 'normal' | null;
    textAlign?: TextAlign | null;
    padding?: Padding | null;
  } | null;
  props?: {
    text?: string | null;
    url?: string | null;
    buttonBackgroundColor?: string | null;
    buttonTextColor?: string | null;
    buttonStyle?: 'rectangle' | 'pill' | 'rounded' | null;
    size?: 'x-small' | 'small' | 'medium' | 'large' | null;
    fullWidth?: boolean | null;
  } | null;
}

interface DividerBlockData {
  style?: {
    backgroundColor?: string | null;
    padding?: Padding | null;
  } | null;
  props?: {
    lineColor?: string | null;
    lineHeight?: number | null;
  } | null;
}

interface SpacerBlockData {
  props?: {
    height?: number | null;
  } | null;
}

interface HtmlBlockData {
  style?: {
    color?: string | null;
    backgroundColor?: string | null;
    fontSize?: number | null;
    fontFamily?: FontFamily | null;
    fontWeight?: 'bold' | 'normal' | null;
    textAlign?: TextAlign | null;
    padding?: Padding | null;
  } | null;
  props?: {
    contents?: string | null;
  } | null;
}

interface Block {
  type: string;
  data: any;
}

export type EmailDocument = Record<string, Block>;

interface EmailBlockEditorProps {
  value: EmailDocument;
  onChange: (doc: EmailDocument) => void;
}

// ── Constants ──

const FONT_FAMILIES: { value: FontFamily; label: string }[] = [
  { value: 'MODERN_SANS', label: 'Modern Sans' },
  { value: 'BOOK_SANS', label: 'Book Sans' },
  { value: 'ORGANIC_SANS', label: 'Organic Sans' },
  { value: 'GEOMETRIC_SANS', label: 'Geometric Sans' },
  { value: 'HEAVY_SANS', label: 'Heavy Sans' },
  { value: 'ROUNDED_SANS', label: 'Rounded Sans' },
  { value: 'MODERN_SERIF', label: 'Modern Serif' },
  { value: 'BOOK_SERIF', label: 'Book Serif' },
  { value: 'MONOSPACE', label: 'Monospace' },
];

const BLOCK_TYPES = [
  { type: 'Text', label: 'Text', icon: Type, description: 'Paragraph with markdown support' },
  { type: 'Heading', label: 'Heading', icon: Heading1, description: 'H1, H2, or H3 heading' },
  { type: 'Image', label: 'Image', icon: ImageIcon, description: 'Image with alt text' },
  {
    type: 'Button',
    label: 'Button',
    icon: RectangleHorizontal,
    description: 'Call-to-action button',
  },
  { type: 'Divider', label: 'Divider', icon: Minus, description: 'Horizontal line separator' },
  { type: 'Spacer', label: 'Spacer', icon: MoveVertical, description: 'Vertical spacing' },
  { type: 'Html', label: 'Raw HTML', icon: Code2, description: 'Custom HTML code' },
];

const DEFAULT_PADDING: Padding = { top: 16, bottom: 16, left: 24, right: 24 };

// ── Default block data factories ──

function createBlockData(type: string): any {
  switch (type) {
    case 'Text':
      return {
        style: { fontSize: 16, padding: { ...DEFAULT_PADDING }, textAlign: 'left' },
        props: { text: 'Your text here...', markdown: true },
      } satisfies TextBlockData;
    case 'Heading':
      return {
        style: { padding: { top: 24, bottom: 8, left: 24, right: 24 }, textAlign: 'left' },
        props: { text: 'Section Heading', level: 'h2' },
      } satisfies HeadingBlockData;
    case 'Image':
      return {
        style: { padding: { ...DEFAULT_PADDING }, textAlign: 'center' },
        props: { url: '', alt: '', width: null, height: null },
      } satisfies ImageBlockData;
    case 'Button':
      return {
        style: { textAlign: 'center', padding: { ...DEFAULT_PADDING } },
        props: {
          text: 'Click Here',
          url: '',
          buttonBackgroundColor: '#6366f1',
          buttonTextColor: '#ffffff',
          buttonStyle: 'rounded',
          size: 'medium',
          fullWidth: false,
        },
      } satisfies ButtonBlockData;
    case 'Divider':
      return {
        style: { padding: { top: 16, bottom: 16, left: 24, right: 24 } },
        props: { lineColor: '#e5e7eb', lineHeight: 1 },
      } satisfies DividerBlockData;
    case 'Spacer':
      return {
        props: { height: 32 },
      } satisfies SpacerBlockData;
    case 'Html':
      return {
        style: { padding: { ...DEFAULT_PADDING } },
        props: { contents: '<!-- Custom HTML -->' },
      } satisfies HtmlBlockData;
    default:
      return {};
  }
}

let _blockCounter = 100;
function generateBlockId(type: string): string {
  return `${type.toLowerCase()}-${Date.now()}-${++_blockCounter}`;
}

// ── Helper: get summary text for collapsed block view ──

function getBlockSummary(block: Block): string {
  switch (block.type) {
    case 'Text':
      return block.data?.props?.text?.slice(0, 60) || 'Empty text';
    case 'Heading':
      return block.data?.props?.text?.slice(0, 60) || 'Empty heading';
    case 'Image':
      return block.data?.props?.url
        ? `Image: ${block.data.props.alt?.slice(0, 40) || block.data.props.url.slice(0, 40)}`
        : 'No image set';
    case 'Button':
      return `"${block.data?.props?.text || 'Button'}" → ${block.data?.props?.url || 'no link'}`;
    case 'Divider':
      return 'Horizontal divider';
    case 'Spacer':
      return `${block.data?.props?.height || 32}px spacing`;
    case 'Html':
      return block.data?.props?.contents?.slice(0, 60) || 'Empty HTML';
    default:
      return block.type;
  }
}

// ── Inline color picker ──

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="w-24 shrink-0 text-xs">{label}</Label>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-7 cursor-pointer rounded border p-0.5"
        />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="h-7 w-24 font-mono text-xs"
        />
      </div>
    </div>
  );
}

// ── Text align buttons ──

function TextAlignButtons({
  value,
  onChange,
}: {
  value: TextAlign;
  onChange: (v: TextAlign) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label className="w-24 shrink-0 text-xs">Align</Label>
      <div className="flex rounded-md border">
        {(['left', 'center', 'right'] as TextAlign[]).map((align) => (
          <button
            key={align}
            type="button"
            onClick={() => onChange(align)}
            className={`px-2.5 py-1 text-xs capitalize ${
              value === align ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            } ${align === 'left' ? 'rounded-l-md' : ''} ${align === 'right' ? 'rounded-r-md' : ''}`}
          >
            {align}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Block config panels ──

function TextBlockConfig({
  data,
  onChange,
}: {
  data: TextBlockData;
  onChange: (d: TextBlockData) => void;
}) {
  const style = data.style || {};
  const props = data.props || {};

  function updateStyle(patch: Partial<NonNullable<TextBlockData['style']>>) {
    onChange({ ...data, style: { ...style, ...patch } });
  }
  function updateProps(patch: Partial<NonNullable<TextBlockData['props']>>) {
    onChange({ ...data, props: { ...props, ...patch } });
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 text-xs">Content (Markdown supported)</Label>
        <Textarea
          value={props.text || ''}
          onChange={(e) => updateProps({ text: e.target.value })}
          placeholder="Write your text here... **bold** *italic* [link](url)"
          className="min-h-[100px] text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 text-xs">Font Size</Label>
          <Input
            type="number"
            min={10}
            max={48}
            value={style.fontSize ?? 16}
            onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) || 16 })}
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label className="mb-1 text-xs">Weight</Label>
          <Select
            value={style.fontWeight || 'normal'}
            onValueChange={(v) => updateStyle({ fontWeight: v as any })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <TextAlignButtons
        value={(style.textAlign as TextAlign) || 'left'}
        onChange={(v) => updateStyle({ textAlign: v })}
      />
      <ColorInput
        label="Text Color"
        value={style.color || ''}
        onChange={(v) => updateStyle({ color: v })}
      />
    </div>
  );
}

function HeadingBlockConfig({
  data,
  onChange,
}: {
  data: HeadingBlockData;
  onChange: (d: HeadingBlockData) => void;
}) {
  const style = data.style || {};
  const props = data.props || {};

  function updateStyle(patch: Partial<NonNullable<HeadingBlockData['style']>>) {
    onChange({ ...data, style: { ...style, ...patch } });
  }
  function updateProps(patch: Partial<NonNullable<HeadingBlockData['props']>>) {
    onChange({ ...data, props: { ...props, ...patch } });
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 text-xs">Heading Text</Label>
        <Input
          value={props.text || ''}
          onChange={(e) => updateProps({ text: e.target.value })}
          placeholder="Your heading..."
          className="text-sm font-semibold"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 text-xs">Level</Label>
          <Select
            value={props.level || 'h2'}
            onValueChange={(v) => updateProps({ level: v as any })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="h1">H1 - Large</SelectItem>
              <SelectItem value="h2">H2 - Medium</SelectItem>
              <SelectItem value="h3">H3 - Small</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 text-xs">Weight</Label>
          <Select
            value={style.fontWeight || 'bold'}
            onValueChange={(v) => updateStyle({ fontWeight: v as any })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="bold">Bold</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <TextAlignButtons
        value={(style.textAlign as TextAlign) || 'left'}
        onChange={(v) => updateStyle({ textAlign: v })}
      />
      <ColorInput
        label="Color"
        value={style.color || ''}
        onChange={(v) => updateStyle({ color: v })}
      />
    </div>
  );
}

function ImageBlockConfig({
  data,
  onChange,
}: {
  data: ImageBlockData;
  onChange: (d: ImageBlockData) => void;
}) {
  const style = data.style || {};
  const props = data.props || {};

  function updateStyle(patch: Partial<NonNullable<ImageBlockData['style']>>) {
    onChange({ ...data, style: { ...style, ...patch } });
  }
  function updateProps(patch: Partial<NonNullable<ImageBlockData['props']>>) {
    onChange({ ...data, props: { ...props, ...patch } });
  }

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 text-xs">Image URL</Label>
        <Input
          value={props.url || ''}
          onChange={(e) => updateProps({ url: e.target.value })}
          placeholder="https://example.com/image.jpg"
          className="text-sm"
        />
      </div>
      <div>
        <Label className="mb-1 text-xs">Alt Text</Label>
        <Input
          value={props.alt || ''}
          onChange={(e) => updateProps({ alt: e.target.value })}
          placeholder="Descriptive text for accessibility"
          className="text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 text-xs">Width (px)</Label>
          <Input
            type="number"
            value={props.width ?? ''}
            onChange={(e) =>
              updateProps({ width: e.target.value ? parseInt(e.target.value) : null })
            }
            placeholder="Auto"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label className="mb-1 text-xs">Height (px)</Label>
          <Input
            type="number"
            value={props.height ?? ''}
            onChange={(e) =>
              updateProps({ height: e.target.value ? parseInt(e.target.value) : null })
            }
            placeholder="Auto"
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div>
        <Label className="mb-1 text-xs">Link URL (optional)</Label>
        <Input
          value={props.linkHref || ''}
          onChange={(e) => updateProps({ linkHref: e.target.value || null })}
          placeholder="https://..."
          className="text-sm"
        />
      </div>
      <TextAlignButtons
        value={(style.textAlign as TextAlign) || 'center'}
        onChange={(v) => updateStyle({ textAlign: v })}
      />
    </div>
  );
}

function ButtonBlockConfig({
  data,
  onChange,
}: {
  data: ButtonBlockData;
  onChange: (d: ButtonBlockData) => void;
}) {
  const style = data.style || {};
  const props = data.props || {};

  function updateStyle(patch: Partial<NonNullable<ButtonBlockData['style']>>) {
    onChange({ ...data, style: { ...style, ...patch } });
  }
  function updateProps(patch: Partial<NonNullable<ButtonBlockData['props']>>) {
    onChange({ ...data, props: { ...props, ...patch } });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 text-xs">Button Text</Label>
          <Input
            value={props.text || ''}
            onChange={(e) => updateProps({ text: e.target.value })}
            placeholder="Click Here"
            className="text-sm"
          />
        </div>
        <div>
          <Label className="mb-1 text-xs">Link URL</Label>
          <Input
            value={props.url || ''}
            onChange={(e) => updateProps({ url: e.target.value })}
            placeholder="https://..."
            className="text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="mb-1 text-xs">Style</Label>
          <Select
            value={props.buttonStyle || 'rounded'}
            onValueChange={(v) => updateProps({ buttonStyle: v as any })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rectangle">Rectangle</SelectItem>
              <SelectItem value="rounded">Rounded</SelectItem>
              <SelectItem value="pill">Pill</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 text-xs">Size</Label>
          <Select
            value={props.size || 'medium'}
            onValueChange={(v) => updateProps({ size: v as any })}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="x-small">Extra Small</SelectItem>
              <SelectItem value="small">Small</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="large">Large</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <ColorInput
        label="BG Color"
        value={props.buttonBackgroundColor || '#6366f1'}
        onChange={(v) => updateProps({ buttonBackgroundColor: v })}
      />
      <ColorInput
        label="Text Color"
        value={props.buttonTextColor || '#ffffff'}
        onChange={(v) => updateProps({ buttonTextColor: v })}
      />
      <TextAlignButtons
        value={(style.textAlign as TextAlign) || 'center'}
        onChange={(v) => updateStyle({ textAlign: v })}
      />
      <div className="flex items-center gap-2">
        <Label className="w-24 shrink-0 text-xs">Full Width</Label>
        <Switch
          checked={Boolean(props.fullWidth)}
          onCheckedChange={(v) => updateProps({ fullWidth: v })}
        />
      </div>
    </div>
  );
}

function DividerBlockConfig({
  data,
  onChange,
}: {
  data: DividerBlockData;
  onChange: (d: DividerBlockData) => void;
}) {
  const props = data.props || {};

  function updateProps(patch: Partial<NonNullable<DividerBlockData['props']>>) {
    onChange({ ...data, props: { ...props, ...patch } });
  }

  return (
    <div className="space-y-3">
      <ColorInput
        label="Line Color"
        value={props.lineColor || '#e5e7eb'}
        onChange={(v) => updateProps({ lineColor: v })}
      />
      <div className="flex items-center gap-2">
        <Label className="w-24 shrink-0 text-xs">Thickness</Label>
        <Input
          type="number"
          min={1}
          max={10}
          value={props.lineHeight ?? 1}
          onChange={(e) => updateProps({ lineHeight: parseInt(e.target.value) || 1 })}
          className="h-7 w-20 text-sm"
        />
        <span className="text-muted-foreground text-xs">px</span>
      </div>
    </div>
  );
}

function SpacerBlockConfig({
  data,
  onChange,
}: {
  data: SpacerBlockData;
  onChange: (d: SpacerBlockData) => void;
}) {
  const props = data.props || {};

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="w-24 shrink-0 text-xs">Height</Label>
        <Input
          type="number"
          min={4}
          max={200}
          value={props.height ?? 32}
          onChange={(e) =>
            onChange({
              ...data,
              props: { ...props, height: parseInt(e.target.value) || 32 },
            })
          }
          className="h-7 w-20 text-sm"
        />
        <span className="text-muted-foreground text-xs">px</span>
      </div>
    </div>
  );
}

function HtmlBlockConfig({
  data,
  onChange,
}: {
  data: HtmlBlockData;
  onChange: (d: HtmlBlockData) => void;
}) {
  const props = data.props || {};

  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1 text-xs">Raw HTML</Label>
        <Textarea
          value={props.contents || ''}
          onChange={(e) => onChange({ ...data, props: { ...props, contents: e.target.value } })}
          placeholder="<div>Your custom HTML...</div>"
          className="min-h-[120px] font-mono text-xs"
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Note: Some email clients strip certain HTML elements. Test across clients before sending.
      </p>
    </div>
  );
}

// ── Block config router ──

function BlockConfigForm({ block, onChange }: { block: Block; onChange: (data: any) => void }) {
  switch (block.type) {
    case 'Text':
      return <TextBlockConfig data={block.data} onChange={onChange} />;
    case 'Heading':
      return <HeadingBlockConfig data={block.data} onChange={onChange} />;
    case 'Image':
      return <ImageBlockConfig data={block.data} onChange={onChange} />;
    case 'Button':
      return <ButtonBlockConfig data={block.data} onChange={onChange} />;
    case 'Divider':
      return <DividerBlockConfig data={block.data} onChange={onChange} />;
    case 'Spacer':
      return <SpacerBlockConfig data={block.data} onChange={onChange} />;
    case 'Html':
      return <HtmlBlockConfig data={block.data} onChange={onChange} />;
    default:
      return <p className="text-muted-foreground text-sm">Unknown block type: {block.type}</p>;
  }
}

// ── Block icon ──

function BlockIcon({ type }: { type: string }) {
  const config = BLOCK_TYPES.find((b) => b.type === type);
  if (!config) return <Code2 className="h-4 w-4" />;
  const Icon = config.icon;
  return <Icon className="h-4 w-4" />;
}

// ── Main component ──

export default function EmailBlockEditor({ value, onChange }: EmailBlockEditorProps) {
  const [expandedBlockId, setExpandedBlockId] = useState<string | null>(null);
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);

  // Ensure root block exists
  const rootBlockId = 'root';
  const rootBlock = value[rootBlockId] as
    | { type: 'EmailLayout'; data: EmailLayoutData }
    | undefined;
  const layoutData: EmailLayoutData = rootBlock?.data || {
    backdropColor: '#f3f4f6',
    canvasColor: '#ffffff',
    textColor: '#262626',
    fontFamily: 'MODERN_SANS',
    childrenIds: [],
  };

  const childrenIds: string[] = layoutData.childrenIds || [];

  // ── Update helpers ──

  const updateLayout = useCallback(
    (patch: Partial<EmailLayoutData>) => {
      const newDoc = {
        ...value,
        [rootBlockId]: {
          type: 'EmailLayout',
          data: { ...layoutData, ...patch },
        },
      };
      onChange(newDoc);
    },
    [value, layoutData, onChange]
  );

  const addBlock = useCallback(
    (type: string) => {
      const id = generateBlockId(type);
      const blockData = createBlockData(type);
      const newDoc: EmailDocument = {
        ...value,
        [id]: { type, data: blockData },
        [rootBlockId]: {
          type: 'EmailLayout',
          data: { ...layoutData, childrenIds: [...childrenIds, id] },
        },
      };
      onChange(newDoc);
      setExpandedBlockId(id);
    },
    [value, layoutData, childrenIds, onChange]
  );

  const removeBlock = useCallback(
    (id: string) => {
      const newDoc = { ...value };
      delete newDoc[id];
      newDoc[rootBlockId] = {
        type: 'EmailLayout',
        data: {
          ...layoutData,
          childrenIds: childrenIds.filter((cid) => cid !== id),
        },
      };
      onChange(newDoc);
      if (expandedBlockId === id) setExpandedBlockId(null);
    },
    [value, layoutData, childrenIds, onChange, expandedBlockId]
  );

  const moveBlock = useCallback(
    (id: string, direction: 'up' | 'down') => {
      const idx = childrenIds.indexOf(id);
      if (idx < 0) return;
      const newIds = [...childrenIds];
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= newIds.length) return;
      [newIds[idx], newIds[targetIdx]] = [newIds[targetIdx], newIds[idx]];
      updateLayout({ childrenIds: newIds });
    },
    [childrenIds, updateLayout]
  );

  const updateBlockData = useCallback(
    (id: string, data: any) => {
      const block = value[id];
      if (!block) return;
      onChange({
        ...value,
        [id]: { ...block, data },
      });
    },
    [value, onChange]
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const block = value[id];
      if (!block) return;
      const newId = generateBlockId(block.type);
      const idx = childrenIds.indexOf(id);
      const newChildrenIds = [...childrenIds];
      newChildrenIds.splice(idx + 1, 0, newId);
      const newDoc: EmailDocument = {
        ...value,
        [newId]: { type: block.type, data: JSON.parse(JSON.stringify(block.data)) },
        [rootBlockId]: {
          type: 'EmailLayout',
          data: { ...layoutData, childrenIds: newChildrenIds },
        },
      };
      onChange(newDoc);
      setExpandedBlockId(newId);
    },
    [value, layoutData, childrenIds, onChange]
  );

  return (
    <div className="space-y-3">
      {/* ── Layout Settings ── */}
      <Card>
        <CardHeader
          className="cursor-pointer pb-3"
          onClick={() => setShowLayoutSettings(!showLayoutSettings)}
        >
          <CardTitle className="flex items-center gap-2 text-sm">
            <Palette className="h-4 w-4" />
            Email Layout Settings
            <ChevronRight
              className={`ml-auto h-4 w-4 transition-transform ${showLayoutSettings ? 'rotate-90' : ''}`}
            />
          </CardTitle>
        </CardHeader>
        {showLayoutSettings && (
          <CardContent className="space-y-3 pt-0">
            <ColorInput
              label="Backdrop"
              value={layoutData.backdropColor || '#f3f4f6'}
              onChange={(v) => updateLayout({ backdropColor: v })}
            />
            <ColorInput
              label="Canvas"
              value={layoutData.canvasColor || '#ffffff'}
              onChange={(v) => updateLayout({ canvasColor: v })}
            />
            <ColorInput
              label="Text Color"
              value={layoutData.textColor || '#262626'}
              onChange={(v) => updateLayout({ textColor: v })}
            />
            <ColorInput
              label="Border"
              value={layoutData.borderColor || ''}
              onChange={(v) => updateLayout({ borderColor: v || null })}
            />
            <div className="flex items-center gap-2">
              <Label className="w-24 shrink-0 text-xs">Font</Label>
              <Select
                value={layoutData.fontFamily || 'MODERN_SANS'}
                onValueChange={(v) => updateLayout({ fontFamily: v as FontFamily })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        )}
      </Card>

      {/* ── Block List ── */}
      {childrenIds.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
          <Type className="text-muted-foreground mb-3 h-8 w-8" />
          <p className="text-muted-foreground text-sm">
            No content blocks yet. Add your first block below.
          </p>
        </div>
      )}

      {childrenIds.map((blockId, index) => {
        const block = value[blockId];
        if (!block) return null;
        const isExpanded = expandedBlockId === blockId;
        const blockConfig = BLOCK_TYPES.find((b) => b.type === block.type);

        return (
          <Card
            key={blockId}
            className={`transition-shadow ${isExpanded ? 'ring-primary/20 shadow-md ring-2' : ''}`}
          >
            {/* Block header - always visible */}
            <div
              className="flex cursor-pointer items-center gap-2 px-4 py-2.5"
              onClick={() => setExpandedBlockId(isExpanded ? null : blockId)}
            >
              <GripVertical className="text-muted-foreground h-4 w-4 shrink-0" />
              <BlockIcon type={block.type} />
              <Badge variant="outline" className="text-xs">
                {blockConfig?.label || block.type}
              </Badge>
              <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                {getBlockSummary(block)}
              </span>

              {/* Actions */}
              <div
                className="flex shrink-0 items-center gap-0.5"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === 0}
                  onClick={() => moveBlock(blockId, 'up')}
                  title="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === childrenIds.length - 1}
                  onClick={() => moveBlock(blockId, 'down')}
                  title="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Settings2 className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => duplicateBlock(blockId)}>
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => removeBlock(blockId)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Block config form - when expanded */}
            {isExpanded && (
              <CardContent className="border-t pt-4">
                <BlockConfigForm
                  block={block}
                  onChange={(data) => updateBlockData(blockId, data)}
                />
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* ── Add Block Button ── */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full border-dashed">
            <Plus className="mr-2 h-4 w-4" />
            Add Block
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-64">
          {BLOCK_TYPES.map((bt) => {
            const Icon = bt.icon;
            return (
              <DropdownMenuItem key={bt.type} onClick={() => addBlock(bt.type)}>
                <Icon className="mr-2 h-4 w-4" />
                <div>
                  <p className="text-sm font-medium">{bt.label}</p>
                  <p className="text-muted-foreground text-xs">{bt.description}</p>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Creates a default empty email document */
export function createEmptyEmailDocument(): EmailDocument {
  return {
    root: {
      type: 'EmailLayout',
      data: {
        backdropColor: '#f3f4f6',
        canvasColor: '#ffffff',
        textColor: '#262626',
        fontFamily: 'MODERN_SANS',
        childrenIds: [],
      },
    },
  };
}
